import moment from 'moment-timezone';
import { TIME_ZONE } from '@silvergatedelivery/constants';
import JSZip from 'jszip';
import { download } from 'utilities/file';
import ExcelJS from 'exceljs';
import { Storage } from 'aws-amplify';
import { loadImage } from 'utilities/image';

const getSanitizedOrdererName = (name) => {
  const result = name
    .replace(/\\/g, '')
    .replace(/\//g, '')
    .replace(/\?/g, '')
    .replace(/\*/g, '')
    .replace(/\[/g, '')
    .replace(/\]/g, '');
  return result;
};

function groupBy(array, keyFn) {
  return array.reduce((result, item) => {
    const key = keyFn(item);
    if (!result[key]) result[key] = [];
    result[key].push(item);
    return result;
  }, {});
}

export default async function issueReceipts({
  groupPurchaseOrders = [],
  selectedMonth = 'YYYY-MM',
  client,
  facility,
  includeUndeliveredOrders = false,
  stampS3Key = null,
}) {
  if (groupPurchaseOrders.length === 0) {
    return;
  }

  const zip = new JSZip();
  const [year, month, day] = moment().tz(TIME_ZONE).format('YYYY-MM-DD').split('-');
  const yearMonthDayTitle = `中華民國${parseInt(year)-1911}年${month}月${day}日`;

  // 依 individual 分組
  const ordersByIndividual = groupBy(groupPurchaseOrders, (o) => o.individual?.id || '未知');

  let receiptNo = 0;
  for (const [, orders] of Object.entries(ordersByIndividual)) {
    if (!orders.length) continue;
    const individual = orders[0].individual;
    // 過濾訂單狀態：如果有勾選未送達訂單，則不過濾，否則只過濾出已送達訂單
    const filteredOrders = includeUndeliveredOrders ? orders : orders.filter((order) => ['delivered', 'completed'].includes(order.status));
    if (!filteredOrders.length) continue;

    const workbook = new ExcelJS.Workbook();
    const sanitizedName = getSanitizedOrdererName(individual?.name || '未知');
    const worksheet = workbook.addWorksheet(sanitizedName, {
      pageSetup: {
        paperSize: 9,
        fitToPage: true,
        fitToWidth: 1,
        horizontalCentered: true,
        verticalCentered: true,
        orientation: 'portrait',
      },
    });

    // 處理公司章
    // 取得圖片副檔名
    const getExt = (path = '') => {
      const m = path.match(/\.([a-z0-9]+)(?:\?|$)/i);
      return m ? m[1].toLowerCase() : 'png';
    };

    const loadStampImageData = async (stampS3Key) => {
      if (!stampS3Key || !String(stampS3Key).trim()) return null;

      try {
        const uri = await Storage.get(stampS3Key);
        const { imageBuffer, width, height } = await loadImage(uri, 150);
        if (!imageBuffer) return null;
        return {
          imageBuffer,
          extension: getExt(uri) || getExt(stampS3Key),
          width,
          height,
        };
      } catch (err) {
        console.error('Failed to load stamp image:', err);
        return null;
      }
    };

    const stampImageData = await loadStampImageData(stampS3Key);

    // 將多筆訂單寫入 worksheet
    getWorksheetGroupPurchase(
      worksheet,
      individual,
      facility,
      filteredOrders,
      yearMonthDayTitle,
      selectedMonth,
      receiptNo,
      stampImageData,
    );

    worksheet.pageSetup.margins = {
      left: 0.236, right: 0.236,
      top: 0.748, bottom: 0.748,
      header: 0.3, footer: 0.3,
    };

    receiptNo += 1;

    try {
      const bufferDataByOrderer = await workbook.xlsx.writeBuffer();
      if (process.env.JEST_WORKER_ID) {
        continue;
      }
      zip.file(`${sanitizedName}_團購請款單_${selectedMonth}.xlsx`, bufferDataByOrderer, { binary: true });
    } catch (e) {
      console.error(e);
    }
  }

  if (process.env.JEST_WORKER_ID) {
    return null;
  }

  const files = Object.keys(zip.files);
  if (files.length !== 0) {
    const zipContent = zip.generate({ type: 'blob' });
    const clientName = client?.name || '測試機構';
    download(`${clientName}__${selectedMonth}團購請款單.zip`, zipContent);
  }
}

function getWorksheetGroupPurchase(
  worksheet,
  client,
  facility,
  groupPurchaseOrders,
  yearMonthDayTitle,
  selectedMonth,
  receiptNo,
  stampImageData,
) {
  if (!Array.isArray(groupPurchaseOrders)) groupPurchaseOrders = [groupPurchaseOrders];
  const { name: clientName, phoneNumber, taxIdNumber } = client || {};
  const { name: facilityName, address: facilityAddress, taxIdNumber: facilityTaxId } = facility || {};
  const date = moment().tz(TIME_ZONE).format('YYYY-MM-DD');


  // 設定欄寬
  worksheet.columns = [
    { width: 22 }, // A
    { width: 10 }, // B
    { width: 9 }, // C
    { width: 16 }, // D
    { width: 10 }, // E
    { width: 8 }, // F
    { width: 10 }, // G
    { width: 10 }, // H
    { width: 5 }, // I
    { width: 15 }, // J
    { width: 8 }, // K
    { width: 8 }, // L
    { width: 7 }, // M
    { width: 10 }, // N
    { width: 10 }, // O
    { width: 10 }, // P
  ];

  const defaultRowHeight = 20;
  const rowCount = worksheet.rowCount;
  for (let i = 1; i <= rowCount; i++) {
    if (i === 1 || i === 2) {
      worksheet.getRow(i).height = 30;
    } else {
      worksheet.getRow(i).height = defaultRowHeight;
    }
  }

  worksheet.getCell('A1').value = `${facilityName}`;
  worksheet.getCell('A1').font = { bold: true, size: 14 };
  worksheet.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };
  worksheet.getCell('A2').value = `${selectedMonth.replace('-', '年')}月 | ${clientName} | 請款單`;
  worksheet.getCell('A2').font = { bold: true, size: 14 };
  worksheet.getCell('A2').alignment = { horizontal: 'center', vertical: 'middle' };
  worksheet.getCell('A3').value = '單位名稱：';
  worksheet.getCell('A3').font = { bold: true };
  worksheet.getCell('D3').value = `${clientName}`;
  const facilityInfo = `${facilityName}\n統一編號：${facilityTaxId}\n登記地址：${facilityAddress.county} ${facilityAddress.district} ${facilityAddress.street}`;
  worksheet.getCell('J3').value = facilityInfo;
  worksheet.getCell('J3').alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  worksheet.getCell('A4').value = '單位地址：';
  worksheet.getCell('A4').font = { bold: true };
  worksheet.getCell('D4').value = `${groupPurchaseOrders[0]?.ordererAddress}`;
  worksheet.getCell('A5').value = '統一編號：';
  worksheet.getCell('A5').font = { bold: true };
  worksheet.getCell('D5').value = `${taxIdNumber}`;
  worksheet.getCell('A6').value = '聯絡人：';
  worksheet.getCell('A6').font = { bold: true };
  worksheet.getCell('D6').value = `${groupPurchaseOrders[0]?.ordererName}`;
  worksheet.getCell('A7').value = '聯絡電話：';
  worksheet.getCell('A7').font = { bold: true };
  worksheet.getCell('D7').value = `${phoneNumber}`;
  worksheet.getCell('J7').value = '提供日期：';
  worksheet.getCell('J7').font = { bold: true };
  worksheet.getCell('K7').value = `${date}`;
  worksheet.getCell('A9').value = '日期';
  worksheet.getCell('A9').font = { bold: true };
  worksheet.getCell('A9').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCCCCC' } };
  worksheet.getCell('C9').value = '項目';
  worksheet.getCell('C9').font = { bold: true };
  worksheet.getCell('C9').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCCCCC' } };
  worksheet.getCell('F9').value = '單價';
  worksheet.getCell('F9').font = { bold: true };
  worksheet.getCell('F9').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCCCCC' } };
  worksheet.getCell('I9').value = '數量';
  worksheet.getCell('I9').font = { bold: true };
  worksheet.getCell('I9').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCCCCC' } };
  worksheet.getCell('K9').value = '單位';
  worksheet.getCell('K9').font = { bold: true };
  worksheet.getCell('K9').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCCCCC' } };
  worksheet.getCell('M9').value = '小計';
  worksheet.getCell('M9').font = { bold: true };
  worksheet.getCell('M9').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCCCCC' } };
  worksheet.getCell('O9').value = '備註';
  worksheet.getCell('O9').font = { bold: true };
  worksheet.getCell('O9').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCCCCC' } };

  // 寫入多筆訂單內容（從第10行開始）
  let rowIdx = 10;
  groupPurchaseOrders.forEach((order) => {
    // 為每個訂單的每個項目建立一行
    order.items.forEach((item, itemIndex) => {
      // 如果是第一個項目，顯示日期，否則留空
      worksheet.getCell(`A${rowIdx}`).value = itemIndex === 0 ? `${order.date}` : '';
      worksheet.getCell(`C${rowIdx}`).value = item.name;
      worksheet.getCell(`F${rowIdx}`).value = `$${item.price}`;
      worksheet.getCell(`I${rowIdx}`).value = item.quantity || 0;
      worksheet.getCell(`K${rowIdx}`).value = '份';
      worksheet.getCell(`M${rowIdx}`).value = { formula: `F${rowIdx}*I${rowIdx}` };
      worksheet.getCell(`O${rowIdx}`).value = '';
      rowIdx++;
    });
  });

  // 總計行
  const totalRowIdx = rowIdx;
  worksheet.getCell(`A${totalRowIdx}`).value = '請款金額總計：';
  worksheet.getCell(`A${totalRowIdx}`).font = { bold: true };
  worksheet.getCell(`A${totalRowIdx}`).alignment = { horizontal: 'center', vertical: 'middle' };
  worksheet.getCell(`M${totalRowIdx}`).value = { formula: `SUM(M10:M${totalRowIdx-1})` };
  worksheet.getCell(`M${totalRowIdx}`).font = { bold: true };

  // 說明區塊行
  const sectionRowIdx = totalRowIdx + 1;
  worksheet.getCell(`A${sectionRowIdx}`).value = '【說明】';
  worksheet.getCell(`A${sectionRowIdx}`).font = { bold: true };
  worksheet.getCell(`A${sectionRowIdx}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCCCCC' } };
  worksheet.getCell(`A${sectionRowIdx}`).border = { top: { style: 'thick' }, left: { style: 'thick' }, bottom: { style: 'thick' }, right: { style: 'thick' } };
  worksheet.getCell(`B${sectionRowIdx}`).border = { top: { style: 'thick' }, bottom: { style: 'thick' } };
  worksheet.getCell(`C${sectionRowIdx}`).border = { top: { style: 'thick' }, bottom: { style: 'thick' } };
  worksheet.getCell(`D${sectionRowIdx}`).border = { top: { style: 'thick' }, bottom: { style: 'thick' } };
  worksheet.getCell(`E${sectionRowIdx}`).border = { top: { style: 'thick' }, bottom: { style: 'thick' }, right: { style: 'thick' } };
  worksheet.getCell(`F${sectionRowIdx}`).value = '【匯款資訊】';
  worksheet.getCell(`F${sectionRowIdx}`).font = { bold: true };
  worksheet.getCell(`F${sectionRowIdx}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCCCCC' } };
  worksheet.getCell(`F${sectionRowIdx}`).border = { top: { style: 'thick' }, left: { style: 'thick' }, bottom: { style: 'thick' }, right: { style: 'thick' } };
  worksheet.getCell(`G${sectionRowIdx}`).border = { top: { style: 'thick' }, bottom: { style: 'thick' } };
  worksheet.getCell(`H${sectionRowIdx}`).border = { top: { style: 'thick' }, bottom: { style: 'thick' } };
  worksheet.getCell(`I${sectionRowIdx}`).border = { top: { style: 'thick' }, bottom: { style: 'thick' } };
  worksheet.getCell(`J${sectionRowIdx}`).border = { top: { style: 'thick' }, bottom: { style: 'thick' }, right: { style: 'thick' } };
  worksheet.getCell(`K${sectionRowIdx}`).value = '【發票章欄位】';
  worksheet.getCell(`K${sectionRowIdx}`).font = { bold: true };
  worksheet.getCell(`K${sectionRowIdx}`).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCCCCC' } };
  worksheet.getCell(`K${sectionRowIdx}`).border = { top: { style: 'thick' }, left: { style: 'thick' }, bottom: { style: 'thick' }, right: { style: 'thick' } };
  worksheet.getCell(`L${sectionRowIdx}`).border = { top: { style: 'thick' }, bottom: { style: 'thick' } };
  worksheet.getCell(`M${sectionRowIdx}`).border = { top: { style: 'thick' }, bottom: { style: 'thick' } };
  worksheet.getCell(`N${sectionRowIdx}`).border = { top: { style: 'thick' }, bottom: { style: 'thick' } };
  worksheet.getCell(`O${sectionRowIdx}`).border = { top: { style: 'thick' }, bottom: { style: 'thick' } };
  worksheet.getCell(`P${sectionRowIdx}`).border = { top: { style: 'thick' }, bottom: { style: 'thick' }, right: { style: 'thick' } };

  // 動態合併儲存格（在寫入所有內容後執行）
  const mergedCells = [];

  // 標題區域合併
  mergedCells.push('A1:P1', 'A2:P2', 'A3:C3', 'D3:I3');

  // 機構資訊區域（動態調整到說明區塊前）
  mergedCells.push(`J3:P6`);

  // 基本資訊區域
  mergedCells.push('A4:C4', 'D4:I4', 'A5:C5', 'D5:I5', 'A6:C6', 'D6:I6', 'A7:C7', 'D7:I7');

  // 表頭
  mergedCells.push('A9:B9', 'C9:E9', 'F9:H9', 'I9:J9', 'K9:L9', 'M9:N9', 'O9:P9');

  // 動態合併訂單行的儲存格
  let currentRow = 10;
  groupPurchaseOrders.forEach((order) => {
    const orderStartRow = currentRow;
    const orderEndRow = currentRow + order.items.length - 1;

    // 如果訂單有多個項目，合併日期欄位
    if (order.items.length > 1) {
      mergedCells.push(`A${orderStartRow}:A${orderEndRow}`);
    }

    // 為每個項目行合併儲存格
    for (let i = 0; i < order.items.length; i++) {
      const itemRow = currentRow + i;
      mergedCells.push(
        `B${itemRow}:B${itemRow}`,
        `C${itemRow}:E${itemRow}`,
        `F${itemRow}:H${itemRow}`,
        `I${itemRow}:J${itemRow}`,
        `K${itemRow}:L${itemRow}`,
        `M${itemRow}:N${itemRow}`,
        `O${itemRow}:P${itemRow}`,
      );
    }

    currentRow += order.items.length;
  });

  // 總計行合併
  mergedCells.push(`A${totalRowIdx}:L${totalRowIdx}`, `M${totalRowIdx}:N${totalRowIdx}`);

  // 說明區域合併
  mergedCells.push(`A${sectionRowIdx}:E${sectionRowIdx}`, `F${sectionRowIdx}:J${sectionRowIdx}`, `K${sectionRowIdx}:P${sectionRowIdx}`);
  mergedCells.push(`A${sectionRowIdx+1}:E${sectionRowIdx+10}`, `F${sectionRowIdx+1}:J${sectionRowIdx+10}`, `K${sectionRowIdx+1}:P${sectionRowIdx+10}`);

  // 去重複的合併範圍
  const uniqueMergedCells = [...new Set(mergedCells)];

  uniqueMergedCells.forEach((range) => {
    try {
      worksheet.mergeCells(range);
    } catch (error) {
      // 如果合併失敗，跳過該範圍
      console.warn(`Failed to merge cells ${range}:`, error.message);
    }
  });

  // 加stampImageData到發票章欄位
  if (stampImageData) {
    const imageId = worksheet.workbook.addImage({
      buffer: stampImageData.imageBuffer,
      extension: stampImageData.extension,
    });
    worksheet.addImage(imageId, {
      tl: { col: 12 + 0.3, row: sectionRowIdx + 0.3 }, // M列，發票區塊中央
      ext: { width: stampImageData.width, height: stampImageData.height },
    });
  }

  return true;
}
